import { useMemo } from 'react';
import * as d3 from 'd3';
import * as styles from '../styles/components/histogram.css.ts';
import { useD3Chart } from '../hooks/useD3Chart';
import { useD3Tooltip } from '../hooks/useD3Tooltip';
import { useD3Selection } from '../hooks/useD3Selection';
import { useLazyVisualization } from '../hooks/useLazyVisualization';
import { useHistogramData } from '../hooks/useHistogramData';
import { calculateKernelDensityEstimation } from '../utils/calculateKernelDensityEstimation';
import { ClearSelectionButton } from './common/ClearSelectionButton';
import { LazyVisualizationPlaceholder } from './common/LazyVisualizationPlaceholder';
import { HistogramFaceted } from './histogram/HistogramFaceted';
import { renderHistogramLegend } from './histogram/HistogramLegend';
import { renderHistogramAnnotations } from './histogram/HistogramAnnotations';
import { renderDensityCurves } from './histogram/HistogramDensity';
import {
  renderGroupedBars,
  renderGroupedSelectionBars,
  renderUngroupedBars,
  renderUngroupedSelectionBars
} from './histogram/HistogramBars';
import { HistogramProps } from '../../types/histogram.types';

/**
 * Histogram - Component that displays histograms for numeric data
 *
 * Features:
 * - Generate histograms from numeric arrays
 * - SVG-based visualization using D3.js
 * - Pan, zoom, and scroll functionality
 * - Responsive design
 * - Tooltip display
 * - Data selection functionality by bar click
 * - Selection state visualization (double histogram display)
 *
 * Operation methods:
 * - Mouse wheel: Zoom in/out
 * - Drag: Pan (move)
 * - Touch swipe: Pan (move)
 * - Bar click: Select data rows in corresponding range (when onBinClick is set)
 * - Cmd/Ctrl+Bar click: Add selection for data rows in corresponding range, or deselect if already fully selected
 *
 * Props:
 * - data: Numeric array (target data for histogram)
 * - width: SVG width (default: 280)
 * - height: SVG height (default: 200)
 * - bins: Number of bins (default: 10)
 * - title: Histogram title
 * - onBinClick: Callback function for bar clicks (receives minValue, maxValue, dataIndices, isAdditive)
 * - originalData: Original data index information (array of {value, index})
 * - selectedIndices: Array of selected row indices (displayed overlaid in red)
 * - columnName: Column name for context menu display
 * - dataset: Dataset for report creation
 * - availableReports: List of reports that can be added to
 * - onAddToReport: Callback when adding to existing report
 * - onCreateNewReport: Callback when creating new report
 */
export function Histogram({
  data,
  groupedData,
  width: defaultWidth = 280,
  height,
  bins = 10,
  title,
  onBinClick,
  originalData,
  selectedIndices = [],
  showDensity = false,
  densityBandwidth,
  orientation = 'vertical',
  groupMode = 'overlay',
  forcedDataRange,
  forcedMaxFrequency,
  showAnnotations,
  margin: customMargin,
  hideControls = false,
  compact = false
}: HistogramProps) {
  // グループ化データまたは単一データを処理
  const isGrouped = !!groupedData && groupedData.length > 0;
  const isFaceted = isGrouped && groupMode === 'faceted';
  const allData = useMemo(() =>
    isGrouped
      ? groupedData!.flatMap(g => g.values)
      : (data || [])
  , [isGrouped, groupedData, data]);

  // データサイズの計算（遅延レンダリングの判定に使用）
  const dataSize = useMemo(() => {
    if (groupedData && groupedData.length > 0) {
      return groupedData.reduce((sum, group) => sum + (group.originalData?.length || group.values.length), 0);
    }
    return originalData?.length || data?.length || 0;
  }, [data, originalData, groupedData]);

  // 遅延可視化フック（1,000行以上の場合はプレースホルダー表示）
  const {
    shouldRender,
    requestRender,
    isLoading,
    error,
    retry,
    notifyRenderComplete
  } = useLazyVisualization({
    initialRender: dataSize < 1000
  });

  // カスタムフックを使用
  const tooltip = useD3Tooltip({
    className: 'histogram-tooltip',
    style: {
      padding: '8px',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      borderRadius: '4px',
      fontSize: '12px',
      pointerEvents: 'none' as const,
      whiteSpace: 'nowrap' as const,
      zIndex: 1000
    }
  });
  const selection = useD3Selection(selectedIndices);

  // マージン計算（customMarginが指定されていない場合はcompactモード、orientation、グループ化に応じて自動計算）
  const margin = useMemo(() =>
    customMargin ?? (compact
      ? (orientation === 'horizontal'
          ? { top: 20, right: 20, bottom: 30, left: 40 }
          : { top: 20, right: 20, bottom: 30, left: 40 })
      : orientation === 'horizontal'
        ? {
            top: title ? 75 : 45,
            right: isGrouped ? 120 : 20,
            bottom: 40,
            left: 80
          }
        : {
            top: title ? 75 : 45,
            right: isGrouped ? 120 : 20,
            bottom: 40,
            left: 50
          })
  , [customMargin, compact, orientation, title, isGrouped]);

  // ヒストグラムデータとスケール設定の計算
  const histogramData = useHistogramData({
    shouldRender,
    allData,
    bins,
    orientation,
    isGrouped,
    groupedData,
    groupMode,
    forcedDataRange,
    forcedMaxFrequency
  });

  // useD3Chartで初期化とサイズ更新を管理
  // enableZoom=true: ズーム機能を統合
  // 使用する高さを決定: propsのheight、それがなければ200
  const effectiveHeight = height ?? 200;

  const { containerRef, svgRef, width, height: actualHeight } = useD3Chart<{
    clipId: string;
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
    xScale: d3.ScaleLinear<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    xAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    yAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    titleText: d3.Selection<SVGTextElement, unknown, null, undefined>;
  }>({
    defaultWidth,
    defaultHeight: effectiveHeight,
    monitorHeight: true,
    enableZoom: true,
    zoomOptions: {
      scaleExtent: [1, 20],
      extent: [[0, 0], [defaultWidth, effectiveHeight]],
      translateExtent: [[-margin.left, -margin.top], [defaultWidth + margin.right, effectiveHeight + margin.bottom]],
      filter: (event) => {
        if (event.type === 'wheel') {
          return (event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey;
        }
        return !(event as MouseEvent).button;
      }
    },
    margin,
    setup: ({ svg, g }) => {
      // クリップパスを作成
      const clipId = `clip-${Math.random().toString(36).substring(2, 11)}`;

      svg.append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect');

      // チャートエリアのグループを作成（クリップパス適用）
      const chartGroup = g.append('g')
        .attr('clip-path', `url(#${clipId})`);

      // 軸グループを作成
      const xAxis = g.append('g')
        .attr('class', 'x-axis');

      const yAxis = g.append('g')
        .attr('class', 'y-axis');

      // タイトル用のテキスト要素を作成
      const titleText = svg.append('text')
        .attr('class', 'histogram-title')
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('fill', '#333');

      // スケールを初期化
      const xScale = d3.scaleLinear();
      const yScale = d3.scaleLinear();

      return {
        clipId,
        chartGroup,
        xScale,
        yScale,
        xAxis,
        yAxis,
        titleText
      };
    },
    render: ({ svg, g, refs, width, innerWidth, innerHeight, currentTransform }) => {
      if (!shouldRender) return;

      const { clipId, chartGroup, xAxis, yAxis, titleText, xScale, yScale } = refs;

      // クリップパスのサイズを更新
      const clipPadding = 20;
      svg.select(`#${clipId} rect`)
        .attr('x', 0)
        .attr('y', -clipPadding)
        .attr('width', innerWidth)
        .attr('height', innerHeight + clipPadding * 2);

      // X軸の位置を更新
      xAxis.attr('transform', `translate(0,${innerHeight})`);

      // タイトルの位置とテキストを更新
      if (title) {
        titleText
          .attr('x', width / 2)
          .attr('y', 15)
          .text(title)
          .style('display', 'block');
      } else {
        titleText.style('display', 'none');
      }

      // スケールのrangeを更新
      xScale.range([0, innerWidth]);
      yScale.range([innerHeight, 0]);

      // スケールのdomainを更新（histogramDataから）
      if (!histogramData) return;

      xScale.domain(histogramData.xDomain);
      yScale.domain(histogramData.yDomain);

      const histData = histogramData.histData || undefined;
      const groupHistData = histogramData.groupHistData || undefined;

      if (!histData && !groupHistData) {
        if (shouldRender) {
          notifyRenderComplete();
        }
        return;
      }
      if (!isGrouped && !histData) {
        if (shouldRender) {
          notifyRenderComplete();
        }
        return;
      }
      if (isGrouped && !groupHistData) {
        if (shouldRender) {
          notifyRenderComplete();
        }
        return;
      }

      // ズーム適用後のスケールを計算
      const zoomTransform = currentTransform || d3.zoomIdentity;
      const currentXScale = orientation === 'horizontal'
        ? xScale
        : zoomTransform.rescaleX(xScale);
      const currentYScale = orientation === 'horizontal'
        ? zoomTransform.rescaleY(yScale)
        : yScale;

      // 密度推定データを計算
      const densityEstimationData = calculateKernelDensityEstimation({
        showDensity,
        allData,
        densityBandwidth,
        xScale,
        yScale,
        histData,
        groupHistData,
        isGrouped,
        groupMode,
        orientation,
        bins
      });

      const densityData = densityEstimationData?.densityData || [];
      const groupDensityData = densityEstimationData?.groupDensityData || [];
      const densityScale = densityEstimationData?.densityScale || 1;
      const originalXDomain = densityEstimationData?.originalXDomain || d3.extent(allData) as [number, number];

      // 軸を更新
      if (orientation === 'horizontal') {
        xAxis.call(d3.axisBottom(xScale).ticks(5))
          .selectAll('text')
          .style('font-size', '11px')
          .style('fill', '#666');

        yAxis.call(d3.axisLeft(currentYScale).ticks(5))
          .selectAll('text')
          .style('font-size', '11px')
          .style('fill', '#666');
      } else {
        xAxis.call(d3.axisBottom(currentXScale).ticks(5))
          .selectAll('text')
          .style('font-size', '11px')
          .style('fill', '#666');

        yAxis.call(d3.axisLeft(yScale).ticks(5))
          .selectAll('text')
          .style('font-size', '11px')
          .style('fill', '#666');
      }

      g.selectAll('.x-axis path, .y-axis path, .x-axis line, .y-axis line')
        .style('stroke', '#ccc');

      // バーを描画
      if (isGrouped && groupHistData) {
        renderGroupedBars({
          chartGroup,
          groupHistData,
          groupMode,
          orientation,
          currentXScale,
          currentYScale,
          xScale,
          yScale,
          innerHeight,
          selection,
          tooltip,
          onBinClick,
          allData,
          histData,
          originalData,
          isGrouped
        });

        renderGroupedSelectionBars({
          chartGroup,
          groupHistData,
          groupMode,
          orientation,
          currentXScale,
          currentYScale,
          xScale,
          yScale,
          innerHeight,
          selection,
          tooltip,
          onBinClick,
          allData,
          histData,
          originalData,
          isGrouped
        });

        // 凡例の描画
        renderHistogramLegend({
          svg,
          groupHistData,
          selectedIndices,
          actualWidth: width,
          marginRight: margin.right,
          marginTop: margin.top,
          groupMode
        });
      } else if (histData) {
        // 凡例をクリア
        svg.selectAll('.legend').remove();

        renderUngroupedBars({
          chartGroup,
          histData,
          orientation,
          currentXScale,
          currentYScale,
          xScale,
          yScale,
          innerHeight,
          selection,
          tooltip,
          onBinClick,
          originalData,
          allData,
          groupHistData,
          isGrouped,
          groupMode
        });

        renderUngroupedSelectionBars({
          chartGroup,
          histData,
          orientation,
          currentXScale,
          currentYScale,
          xScale,
          yScale,
          innerHeight,
          selection,
          tooltip,
          onBinClick,
          originalData,
          allData,
          groupHistData,
          isGrouped,
          groupMode
        });
      }

      // 密度曲線を描画
      renderDensityCurves({
        chartGroup,
        showDensity,
        densityData,
        groupDensityData,
        densityScale,
        isGrouped,
        groupMode,
        groupHistData,
        orientation,
        currentXScale,
        currentYScale,
        xScale,
        yScale,
        bins,
        originalXDomain
      });

      // 統計量アノテーションの描画
      renderHistogramAnnotations({
        chartGroup,
        showAnnotations,
        allData,
        orientation,
        currentXScale,
        currentYScale,
        innerWidth,
        innerHeight
      });

      if (shouldRender) {
        notifyRenderComplete();
      }
    },
    dependencies: [
      shouldRender, title, histogramData,
      allData, bins, originalData, onBinClick, showDensity, densityBandwidth,
      orientation, isGrouped, groupedData, groupMode,
      margin, selection, tooltip, selectedIndices,
      notifyRenderComplete, showAnnotations
    ]
  });

  // 遅延レンダリング：プレースホルダー表示（SVGは常にレンダリングしてDOMにマウントする）
  const showPlaceholder = !shouldRender || error;

  // データの存在チェック
  if (!isGrouped && (!data || data.length === 0)) {
    return (
      <div className={styles.histogramEmpty} style={{
        width: width,
        height: effectiveHeight
      }}>
        データがありません
      </div>
    );
  }

  if (isGrouped && (!groupedData || groupedData.length === 0)) {
    return (
      <div className={styles.histogramEmpty} style={{
        width: width,
        height: effectiveHeight
      }}>
        データがありません
      </div>
    );
  }

  // Facetedモードの場合
  if (isFaceted && groupedData) {
    return (
      <HistogramFaceted
        groupedData={groupedData}
        bins={bins}
        width={width}
        height={actualHeight}
        orientation={orientation}
        selectedIndices={selectedIndices}
        onBinClick={onBinClick}
        showDensity={showDensity}
        densityBandwidth={densityBandwidth}
        showAnnotations={showAnnotations}
        HistogramComponent={Histogram}
      />
    );
  }

  return (
    <div ref={containerRef} className={styles.histogramContainer} style={{ width: '100%', height: height ?? '100%' }}>
      <div style={{ position: 'relative' }}>
        <svg
          ref={svgRef}
          width={width}
          height={actualHeight}
          className={styles.histogramSvg}
          style={{
            maxWidth: '100%'
          }}
        ></svg>

        {/* 遅延レンダリングのプレースホルダー（SVGの上に重ねて表示） */}
        {showPlaceholder && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
            <LazyVisualizationPlaceholder
              dataSize={dataSize}
              onView={requestRender}
              visualizationType="histogram"
              height={actualHeight}
              isLoading={isLoading}
              error={error}
              onRetry={retry}
            />
          </div>
        )}

        {/* オーバーレイボタン */}
        {!hideControls && !showPlaceholder && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          gap: '4px',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          padding: '2px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          pointerEvents: 'none'
        }}>
          <div
            title="Pan mode"
            style={{
              width: '28px',
              height: '28px',
              padding: '0',
              border: 'none',
              borderRadius: '4px',
              background: 'rgba(66, 133, 244, 0.2)',
              cursor: 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L6 3h1.5v4.5H3V6L1 8l2 2v-1.5h4.5V13H6l2 2 2-2H8.5V8.5H13V10l2-2-2-2v1.5H8.5V3H10L8 1z"
                fill="#4285f4" />
            </svg>
          </div>
          <ClearSelectionButton
            onClear={() => onBinClick?.(0, 0, [], false)}
            hasSelection={selectedIndices.length > 0}
            style={{
              marginLeft: '4px',
              borderLeft: '1px solid rgba(0, 0, 0, 0.1)',
              pointerEvents: 'auto'
            }}
          />
        </div>
        )}
      </div>
    </div>
  );
}
